import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CalendarClock, Check, ChevronDown, ChevronRight, Flag, Inbox, FileText } from "lucide-react";
import type { Mutation, PushOption, Step, Task } from "@tunic/shared";
import { useMe } from "@/api/auth";
import { api } from "@/api/client";
import { useInvalidateProject } from "@/api/projects";
import { useNow, type NowItem } from "@/api/now";
import { useUndoToast } from "@/hooks/useUndoToast";
import { Page, PageHeader } from "@/components/shell/PageHeader";
import { HealthBadge } from "@/components/projects/HealthBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/")({ component: NowPage });

const KIND_ICON = {
  milestone_overdue: Flag,
  milestone_at_risk: Flag,
  task_overdue: AlertTriangle,
  task_due_soon: CalendarClock,
  task_snoozed: CalendarClock,
  inbox_item: Inbox,
  report_unread: FileText,
  stale_project: AlertTriangle,
} as const;

function NowPage() {
  const me = useMe();
  const now = useNow();
  const [showRest, setShowRest] = useState(false);
  const items = now.data?.items ?? [];
  const [hero, ...others] = items;
  const alternates = others.slice(0, 3);
  const rest = others.slice(3);
  const first = me.data?.user.name.split(" ")[0] ?? "";

  return (
    <>
      <PageHeader title={`Hello ${first}`} subtitle={items.length === 0 ? "Nothing needs you right now." : `${items.length} thing${items.length === 1 ? "" : "s"} could use you`} />
      <Page className="space-y-5">
        {now.data && items.length === 0 && (
          <EmptyState title="All clear" hint="No overdue milestones, no due tasks, nothing waiting in the inbox. Go build something." action={<Button asChild variant="outline"><Link to="/projects">See projects</Link></Button>} />
        )}
        {hero && <ItemCard item={hero} hero />}
        {alternates.length > 0 && (
          <div className="grid gap-3 md:grid-cols-3">
            {alternates.map((i) => (
              <ItemCard key={i.id} item={i} />
            ))}
          </div>
        )}
        {rest.length > 0 && (
          <div>
            <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={() => setShowRest((x) => !x)}>
              {showRest ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />} Everything else ({rest.length})
            </button>
            {showRest && (
              <ul className="mt-2 divide-y rounded-lg border bg-card">
                {rest.map((i) => (
                  <ItemRow key={i.id} item={i} />
                ))}
              </ul>
            )}
          </div>
        )}
      </Page>
    </>
  );
}

function useItemActions(item: NowItem) {
  const inv = useInvalidateProject();
  const undoToast = useUndoToast();
  const navigate = useNavigate();
  const done = useMutation({ mutationFn: () => api.post<Mutation<Task>>(`/api/tasks/${item.entity.id}/done`), onSuccess: () => inv(item.project?.id) });
  const push = useMutation({
    mutationFn: (option: PushOption) => api.post<Mutation<Task | Step>>(`/api/${item.entity.type === "step" ? "steps" : "tasks"}/${item.entity.id}/push`, { option }),
    onSuccess: () => inv(item.project?.id),
  });
  const open = () => {
    if (item.entity.type === "source") navigate({ to: "/inbox" });
    else if (item.entity.type === "report") navigate({ to: "/reports" });
    else if (item.project) navigate({ to: "/projects/$projectId", params: { projectId: item.project.slug } });
  };
  return {
    open,
    done: () => undoToast.run(done.mutateAsync(), `Done: ${item.title}`),
    push: (o: PushOption) => undoToast.run(push.mutateAsync(o), "Pushed out"),
    busy: done.isPending || push.isPending,
  };
}

function Actions({ item, size = "sm" }: { item: NowItem; size?: "sm" | "default" }) {
  const a = useItemActions(item);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {item.actions.includes("done") && (
        <Button size={size} onClick={a.done} disabled={a.busy}>
          <Check className="size-3.5" /> Done
        </Button>
      )}
      {item.actions.includes("push") && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size={size} variant="outline" disabled={a.busy}>
              Push <ChevronDown className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => a.push("tomorrow")}>Tomorrow</DropdownMenuItem>
            <DropdownMenuItem onClick={() => a.push("next_monday")}>Next Monday</DropdownMenuItem>
            <DropdownMenuItem onClick={() => a.push("next_week")}>A week</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {item.actions.includes("review") && (
        <Button size={size} onClick={a.open}>
          Review
        </Button>
      )}
      <Button size={size} variant="ghost" onClick={a.open}>
        Open <ArrowRight className="size-3.5" />
      </Button>
    </div>
  );
}

function ItemCard({ item, hero }: { item: NowItem; hero?: boolean }) {
  const Icon = KIND_ICON[item.kind];
  const urgent = item.kind === "milestone_overdue" || item.kind === "task_overdue" || item.kind === "stale_project";
  return (
    <div className={cn("rounded-lg border bg-card", hero ? "p-5" : "flex flex-col p-4")}>
      <div className="flex items-start gap-3">
        <Icon className={cn("mt-0.5 shrink-0", hero ? "size-5" : "size-4", urgent ? "text-danger" : "text-warn")} />
        <div className="min-w-0 flex-1">
          <div className={cn("font-semibold", hero ? "text-lg" : "text-sm")}>{item.title}</div>
          <div className={cn("text-muted-foreground", hero ? "text-sm" : "text-xs")}>
            {item.why}
            {item.assignee_name && ` · ${item.assignee_name}`}
          </div>
          {item.project && (
            <div className="mt-1 flex items-center gap-2 text-xs">
              <Link to="/projects/$projectId" params={{ projectId: item.project.slug }} className="text-muted-foreground hover:underline">
                {item.project.name}
              </Link>
              <HealthBadge health={item.project.health} className="scale-90" />
            </div>
          )}
        </div>
      </div>
      <div className={cn(hero ? "mt-4" : "mt-3")}>
        <Actions item={item} size={hero ? "default" : "sm"} />
      </div>
    </div>
  );
}

function ItemRow({ item }: { item: NowItem }) {
  const Icon = KIND_ICON[item.kind];
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-sm">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <span className="font-medium">{item.title}</span>
        <span className="ml-2 text-xs text-muted-foreground">
          {item.why}
          {item.project && ` · ${item.project.name}`}
        </span>
      </div>
      <Actions item={item} />
    </li>
  );
}
